import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Trash2, Plus, Save, X, Loader2 } from "lucide-react";
import ImageUploadField from "./ImageUploadField";

const empty = {
    name: "",
    description: "",
    price: 0,
    duration_min: 60,
    image: null,
    included_service_ids: [],
    highlight_text: "",
    category: "",
    display_order: 0,
    is_active: true,
    featured: true,
};

/**
 * AdminPackages — full CRUD for special packages.
 * Used by AdminDashboard when the "packages" tab is active.
 */
export default function AdminPackages() {
    const [packages, setPackages] = useState([]);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // package being edited, or `empty` for create
    const [saving, setSaving] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const [p, s] = await Promise.all([
                api.get("/packages").then((r) => r.data),
                api.get("/services").then((r) => r.data),
            ]);
            setPackages(p || []);
            setServices(s || []);
        } catch { /* noop */ } finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, []);

    const servicesById = useMemo(() => Object.fromEntries(services.map((s) => [s.id, s])), [services]);

    const save = async () => {
        if (!editing.name || !editing.description || !editing.price) {
            toast.error("Name, description and price are required");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                name: editing.name.trim(),
                description: editing.description,
                price: Number(editing.price) || 0,
                duration_min: Number(editing.duration_min) || 0,
                image: editing.image || null,
                included_service_ids: editing.included_service_ids || [],
                highlight_text: editing.highlight_text || null,
                category: editing.category || null,
                display_order: Number(editing.display_order) || 0,
                is_active: !!editing.is_active,
                featured: !!editing.featured,
            };
            if (editing.id) {
                await api.put(`/packages/${editing.id}`, payload);
                toast.success("Package updated");
            } else {
                await api.post("/packages", payload);
                toast.success("Package created");
            }
            setEditing(null);
            await refresh();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, "Save failed"));
        } finally {
            setSaving(false);
        }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this package? This cannot be undone.")) return;
        try {
            await api.delete(`/packages/${id}`);
            toast.success("Package deleted");
            await refresh();
        } catch { toast.error("Delete failed"); }
    };

    const toggleIncluded = (sid) => {
        setEditing((prev) => {
            const list = new Set(prev.included_service_ids || []);
            if (list.has(sid)) list.delete(sid); else list.add(sid);
            return { ...prev, included_service_ids: Array.from(list) };
        });
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[0.65rem] tracking-[0.22em] uppercase text-[#8F622C] flex items-center gap-2"><Sparkles size={12}/> Special Packages</div>
                    <h2 className="font-serif-display text-2xl text-[#3D0C1F] mt-1">Bundle & standalone offerings</h2>
                    <p className="text-xs text-[#7A5262] mt-1">Anything you list here appears on <code className="text-[#8F622C]">/packages</code>, on the booking page, and is bookable in groups.</p>
                </div>
                <button onClick={() => setEditing({ ...empty })} className="btn-gold inline-flex items-center gap-2" data-testid="new-package">
                    <Plus size={14}/> New Package
                </button>
            </div>

            {loading ? (
                <div className="text-[#7A5262] text-sm py-8 text-center flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin"/> Loading…</div>
            ) : packages.length === 0 ? (
                <div className="text-[#7A5262] text-sm py-16 text-center border border-[#6B1535]/15">No packages yet — create your first one above.</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {packages.map((p) => (
                        <div key={p.id} className="p-5 border border-[#6B1535]/15 bg-[#FDF0EC] flex gap-4" data-testid={`package-row-${p.id}`}>
                            <div className="w-24 h-24 bg-[#FDF0EC] flex-shrink-0 flex items-center justify-center overflow-hidden">
                                {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover"/> : <Sparkles size={22} className="text-[#8F622C]/40"/>}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                    <h3 className="font-serif-display text-lg text-[#3D0C1F] leading-tight truncate">{p.name}</h3>
                                    <span className="text-[#8F622C] font-semibold whitespace-nowrap">S${p.price}</span>
                                </div>
                                <div className="flex flex-wrap items-center gap-2 text-[0.6rem] tracking-[0.2em] uppercase text-[#7A5262] mt-1">
                                    {p.category && <span>{p.category}</span>}
                                    <span>· {(p.included_service_ids || []).length ? `${p.included_service_ids.length}-svc bundle` : "standalone"}</span>
                                    <span>· {p.duration_min || 0} min</span>
                                    {!p.is_active && <span className="text-red-700">· inactive</span>}
                                    <span>· {p.booking_count || 0} booked</span>
                                </div>
                                <p className="text-xs text-[#7A5262] mt-2 line-clamp-2">{p.description}</p>
                                <div className="flex gap-2 mt-3">
                                    <button onClick={() => setEditing({ ...empty, ...p })} className="border border-[#C29A5B]/40 text-[#8F622C] px-3 py-1 text-xs hover:bg-[#6B1535] hover:text-[#FFF8F2]" data-testid={`edit-package-${p.id}`}>Edit</button>
                                    <button onClick={() => del(p.id)} className="border border-red-500/40 text-red-700 px-3 py-1 text-xs hover:bg-red-500/10 inline-flex items-center gap-1" data-testid={`delete-package-${p.id}`}><Trash2 size={11}/> Delete</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <div className="fixed inset-0 z-50 bg-[#3D0C1F]/45 backdrop-blur-sm backdrop-blur-sm flex items-center justify-center p-4" data-testid="edit-package-modal">
                    <div className="bg-[#FBF6F0] border border-[#C29A5B]/40 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-[#6B1535]/15 sticky top-0 bg-[#FBF6F0] z-10">
                            <h3 className="font-serif-display text-xl text-[#3D0C1F]">{editing.id ? "Edit package" : "New package"}</h3>
                            <button onClick={() => setEditing(null)} className="text-[#7A5262] hover:text-[#3D0C1F] p-2" aria-label="Close" data-testid="close-package-modal"><X size={18}/></button>
                        </div>
                        <div className="px-6 py-5 space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <label className="block col-span-2">
                                    <span className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262]">Name</span>
                                    <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-3 py-2.5 mt-1.5 focus:border-[#C29A5B] outline-none text-[#3D0C1F]" data-testid="pkg-name"/>
                                </label>
                                <label className="block col-span-2">
                                    <span className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262]">Description</span>
                                    <textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-3 py-2.5 mt-1.5 focus:border-[#C29A5B] outline-none text-[#3D0C1F]" data-testid="pkg-description"/>
                                </label>
                                <label className="block">
                                    <span className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262]">Price (S$)</span>
                                    <input type="number" min="0" step="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-3 py-2.5 mt-1.5 focus:border-[#C29A5B] outline-none text-[#3D0C1F]" data-testid="pkg-price"/>
                                </label>
                                <label className="block">
                                    <span className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262]">Duration (min)</span>
                                    <input type="number" min="0" step="5" value={editing.duration_min} onChange={(e) => setEditing({ ...editing, duration_min: e.target.value })} className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-3 py-2.5 mt-1.5 focus:border-[#C29A5B] outline-none text-[#3D0C1F]" data-testid="pkg-duration"/>
                                </label>
                                <label className="block">
                                    <span className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262]">Category (optional)</span>
                                    <input value={editing.category || ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })} placeholder="Bridal, Anniversary…" className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-3 py-2.5 mt-1.5 focus:border-[#C29A5B] outline-none text-[#3D0C1F]" data-testid="pkg-category"/>
                                </label>
                                <label className="block">
                                    <span className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262]">Highlight badge (optional)</span>
                                    <input value={editing.highlight_text || ""} onChange={(e) => setEditing({ ...editing, highlight_text: e.target.value })} placeholder="Best Value, Popular…" className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-3 py-2.5 mt-1.5 focus:border-[#C29A5B] outline-none text-[#3D0C1F]" data-testid="pkg-highlight"/>
                                </label>
                                <label className="block">
                                    <span className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262]">Display order</span>
                                    <input type="number" value={editing.display_order} onChange={(e) => setEditing({ ...editing, display_order: e.target.value })} className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-3 py-2.5 mt-1.5 focus:border-[#C29A5B] outline-none text-[#3D0C1F]" data-testid="pkg-order"/>
                                </label>
                                <div className="flex flex-col gap-2 pt-6">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} className="accent-[#6B1535]" data-testid="pkg-active"/>
                                        <span className="text-sm text-[#3D0C1F]">Active (visible to customers)</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} className="accent-[#6B1535]" data-testid="pkg-featured"/>
                                        <span className="text-sm text-[#3D0C1F]">Featured (show on landing tab)</span>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <span className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262]">Image</span>
                                <div className="mt-2">
                                    <ImageUploadField value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} testIdPrefix="pkg-image" />
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center justify-between">
                                    <span className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262]">Included services (bundle)</span>
                                    <span className="text-[0.6rem] text-[#8A6574]">Leave empty for standalone package</span>
                                </div>
                                <div className="mt-2 max-h-56 overflow-y-auto border border-[#6B1535]/15 p-2 space-y-1">
                                    {services.length === 0 && <p className="text-xs text-[#7A5262]">No services to bundle yet.</p>}
                                    {services.map((s) => (
                                        <label key={s.id} className="flex items-center gap-2 text-xs cursor-pointer p-1 hover:bg-[#6B1535]/5">
                                            <input type="checkbox" checked={(editing.included_service_ids || []).includes(s.id)} onChange={() => toggleIncluded(s.id)} className="accent-[#6B1535]" data-testid={`pkg-include-${s.id}`}/>
                                            <span className="text-[#3D0C1F]">{s.name}</span>
                                            <span className="text-[#7A5262] ml-auto">S${s.price}</span>
                                        </label>
                                    ))}
                                </div>
                                {(editing.included_service_ids || []).length > 0 && (
                                    <p className="text-[0.65rem] text-[#7A5262] mt-2">{editing.included_service_ids.length} service{editing.included_service_ids.length !== 1 ? "s" : ""} bundled. Names shown to customer: {(editing.included_service_ids || []).map((sid) => servicesById[sid]?.name).filter(Boolean).join(", ")}</p>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#6B1535]/15 bg-[#FBF6F0] sticky bottom-0">
                            <button onClick={() => setEditing(null)} className="px-4 py-2 text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262] hover:text-[#3D0C1F]" data-testid="cancel-package">Cancel</button>
                            <button onClick={save} disabled={saving} className="btn-gold inline-flex items-center gap-2 disabled:opacity-50" data-testid="save-package">
                                {saving && <Loader2 size={14} className="animate-spin"/>} <Save size={14}/> Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
